// src/routes/MediaSelection/SelectPlanScreen.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Container,
  CircularProgress,
  Alert
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { selectionCard, selectionCardButton, designTokens } from '../../theme/designTokens';

const theme = designTokens.featureThemes.media_selection;

const SelectionCard = styled(Paper)(({ theme: muiTheme }) => ({
  ...selectionCard('media_selection', muiTheme),
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
}));

const ActionButton = styled(Button)(({ theme: muiTheme }) => ({
  ...selectionCardButton('media_selection'),
}));

/**
 * SelectPlanScreen:
 *   - 決定済みの採用計画リストを取得して表示
 *   - ユーザーが選択したプランIDを onSelectPlan(planId) で親に渡す
 *
 * ポイント:
 *   - 「新規で媒体選定を始める」ボタンを必ず表示する。
 *   - onSelectPlan(null) を呼び出すことで新規作成フローに移行可能。
 */
export default function SelectPlanScreen({ onSelectPlan }) {
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const [plans, setPlans] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // /media_selection/decided_plans から決定済みの採用計画を取得
    axios
      .get(`${BACKEND_URL}/media_selection/decided_plans`, { withCredentials: true })
      .then((resp) => {
        // resp.data = [{ id, title, ... }] という想定
        setPlans(resp.data);
      })
      .catch((err) => {
        console.error(err);
        setErrorMsg('決定済みの採用計画を取得できませんでした。');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [BACKEND_URL]);

  // 計画を選択した場合 (planId=number)、または 新規作成 (planId=null)
  const handleSelect = (planId) => {
    onSelectPlan(planId);
  };

  // Container animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      },
    },
  };

  // Card animation
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      },
    },
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        sx={{ textAlign: 'center', mb: 6 }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: theme.primary,
          }}
        >
          採用計画を選択してください
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          どの採用計画をもとに媒体選定を行いますか？
        </Typography>
      </Box>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          {errorMsg}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} sx={{ color: theme.primary }} />
        </Box>
      )}

      {!loading && !errorMsg && (
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <AnimatePresence>
              {/* 既存の採用計画カード */}
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <SelectionCard
                    component={motion.div}
                    variants={cardVariants}
                    whileHover={{ y: -8 }}
                    onClick={() => handleSelect(plan.id)}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                      {/* Icon */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '12px',
                            backgroundColor: `${theme.primary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <AssignmentIcon sx={{ fontSize: 32, color: theme.primary }} />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#212529',
                            mb: 1,
                          }}
                        >
                          {plan.title || `採用計画 #${plan.id}`}
                        </Typography>
                        {plan.summary && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              lineHeight: 1.6,
                              mb: 2,
                            }}
                          >
                            {plan.summary}
                          </Typography>
                        )}
                        {plan.role && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.primary,
                              fontWeight: 500,
                            }}
                          >
                            職種: {plan.role}
                          </Typography>
                        )}
                      </Box>

                      {/* Action Button */}
                      <ActionButton
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(plan.id);
                        }}
                      >
                        この計画で選定する
                      </ActionButton>
                    </Box>
                  </SelectionCard>
                </Grid>
              ))}

              {/* 新規作成カード */}
              <Grid item xs={12} sm={6} md={4}>
                <SelectionCard
                  component={motion.div}
                  variants={cardVariants}
                  whileHover={{ y: -8 }}
                  onClick={() => handleSelect(null)}
                  sx={{
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                  }}
                >
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                    {/* Icon */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '12px',
                          backgroundColor: `${theme.primary}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AddIcon sx={{ fontSize: 32, color: theme.primary }} />
                      </Box>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flexGrow: 1, textAlign: 'center', mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#212529',
                          mb: 1,
                        }}
                      >
                        新規で媒体選定を始める
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        採用計画を使わずに、最初から媒体選定を始めます
                      </Typography>
                    </Box>

                    {/* Action Button */}
                    <ActionButton
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(null);
                      }}
                    >
                      新規で始める
                    </ActionButton>
                  </Box>
                </SelectionCard>
              </Grid>
            </AnimatePresence>
          </Grid>

          {plans.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                決定済みの採用計画がまだありません
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                新規で媒体選定を始めるか、先に採用計画を作成してください
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}
